"use strict";

import _defineProperty from "@babel/runtime-corejs3/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.replace.js";
import "core-js/modules/web.dom-collections.iterator.js";
/**
 * Feature Flag Sync Manager for DataDog
 *
 * Coordinates feature flag tracking across multiple MFEs:
 * - Tracks all flags registered by each MFE
 * - Debounces updates to avoid spamming DataDog API
 * - Persists flag state via window for session history
 * - Syncs mount-critical flags from window.__AG_FEATURE_FLAGS__
 */

// IMPORTANT: We avoid importing from ./agent directly to prevent circular imports.
// agent.ts imports from this file, so we access getDataDogRUM lazily at runtime.
// The type is duplicated here to avoid the circular dependency.

const getDataDogRUM = () => {
  var _DD_RUM;
  if (!process.__browser__) {
    return null;
  }
  return (_DD_RUM = window.DD_RUM) !== null && _DD_RUM !== void 0 ? _DD_RUM : null;
};
const FF_DATADOG_STATE_KEY = '__AG_FF_DATADOG_STATE__';
const SYNC_DEBOUNCE_MS = 100;
/**
 * Normalise MFE name for DataDog keys.
 * Converts `@autoguru/fmo-booking` → `fmo_booking`
 */
const normaliseMfeName = name => name.replace('@autoguru/', '').replace(/-/g, '_');

/**
 * Normalise flag name for DataDog keys.
 * Converts `fcib-enabled` → `fcib_enabled`
 */
const normaliseFlagName = name => name.replace(/-/g, '_');

/**
 * Get or initialise the feature flag state from window.
 */
const getState = () => {
  if (!process.__browser__) {
    return {
      mfeFlags: {},
      mountFlags: {},
      lastSync: 0
    };
  }
  if (!window[FF_DATADOG_STATE_KEY]) {
    window[FF_DATADOG_STATE_KEY] = {
      mfeFlags: {},
      mountFlags: {},
      lastSync: 0
    };
  }
  return window[FF_DATADOG_STATE_KEY];
};

/**
 * Debounced sync timeout ID.
 */
let syncTimeoutId = null;

/**
 * Perform the actual sync to DataDog global context.
 */
const performSync = () => {
  const rum = getDataDogRUM();
  if (!rum) {
    return;
  }
  const state = getState();
  let activeCount = 0;
  const activeMfes = [];

  // Sync mount-critical flags as ff_mount.flag_name
  for (const [flag, value] of Object.entries(state.mountFlags)) {
    rum.setGlobalContextProperty(`ff_mount.${normaliseFlagName(flag)}`, value);
    if (value) {
      activeCount++;
    }
  }

  // Sync per-MFE flags as ff.mfe_name.flag_name
  for (const [mfe, flags] of Object.entries(state.mfeFlags)) {
    const normalisedMfe = normaliseMfeName(mfe);
    activeMfes.push(normalisedMfe);
    for (const [flag, value] of Object.entries(flags)) {
      rum.setGlobalContextProperty(`ff.${normalisedMfe}.${normaliseFlagName(flag)}`, value);
      if (value) {
        activeCount++;
      }
    }
  }

  // Sync summary properties for easy filtering
  rum.setGlobalContextProperty('ff_active_count', activeCount);
  rum.setGlobalContextProperty('ff_mfes', activeMfes);

  // Update last sync timestamp
  state.lastSync = Date.now();
};

/**
 * Schedule a debounced sync to DataDog.
 * Multiple rapid calls will be batched into a single sync.
 */
const scheduleDebouncedSync = () => {
  if (!process.__browser__) {
    return;
  }
  if (syncTimeoutId !== null) {
    clearTimeout(syncTimeoutId);
  }
  syncTimeoutId = setTimeout(() => {
    syncTimeoutId = null;
    performSync();
  }, SYNC_DEBOUNCE_MS);
};

/**
 * Register feature flags for an MFE.
 * Called by FeatureFlagsProvider when flags are resolved.
 *
 * @param mfeName - The MFE package name (e.g., '@autoguru/fmo-booking')
 * @param flags - Map of flag names to their boolean values
 */
export const registerMfeFeatureFlags = (mfeName, flags) => {
  if (!process.__browser__) {
    return;
  }
  const state = getState();

  // Store flags for this MFE (merge with existing if re-registering)
  state.mfeFlags[mfeName] = _objectSpread(_objectSpread({}, state.mfeFlags[mfeName]), flags);
  scheduleDebouncedSync();
};

/**
 * Sync mount-critical feature flags from window.__AG_FEATURE_FLAGS__.
 * Called by DataDog agent after RUM initialisation.
 */
export const syncMountCriticalFlags = () => {
  if (!process.__browser__) {
    return;
  }
  const mountFlags = window.__AG_FEATURE_FLAGS__;
  if (!mountFlags) {
    return;
  }
  const state = getState();
  state.mountFlags = _objectSpread({}, mountFlags);

  // Sync immediately for mount-critical flags (no debounce)
  performSync();
};

/**
 * Get the current feature flag state.
 * Useful for debugging and inspection.
 */
export const getFeatureFlagState = () => {
  var _window$FF_DATADOG_ST;
  if (!process.__browser__) {
    return null;
  }
  return (_window$FF_DATADOG_ST = window[FF_DATADOG_STATE_KEY]) !== null && _window$FF_DATADOG_ST !== void 0 ? _window$FF_DATADOG_ST : null;
};

/**
 * Force an immediate sync to DataDog.
 * Bypasses the debounce mechanism.
 */
export const forceSyncFeatureFlags = () => {
  if (!process.__browser__) {
    return;
  }
  if (syncTimeoutId !== null) {
    clearTimeout(syncTimeoutId);
    syncTimeoutId = null;
  }
  performSync();
};

/**
 * Reset the feature flag state.
 * Only exported for testing purposes.
 */
export const __resetFeatureFlagStateForTesting = () => {
  if (!process.__browser__) {
    return;
  }
  if (syncTimeoutId !== null) {
    clearTimeout(syncTimeoutId);
    syncTimeoutId = null;
  }
  delete window[FF_DATADOG_STATE_KEY];
};